// ============================================
// CLAUSULASCOTIZACIONADMIN.JS — catalogo de "Terminos y condiciones" (TERMINOS-COTIZACION)
// ============================================
// Vive en Administracion > Datos de la empresa, junto al resto de textos que salen en la
// cotizacion. Direccion crea, edita, reordena y activa/desactiva clausulas; el resto las ve en
// solo lectura (el servidor lo hace cumplir con soloDireccion: esto solo evita botones que
// darian 403).
//
// LO QUE HAY QUE TENER CLARO AL EDITAR (y la pantalla lo dice): cambiar una clausula NO toca
// ninguna cotizacion ya guardada. Cada cotizacion lleva su propia copia del texto. El cambio
// solo afecta a las cotizaciones nuevas y a las que marquen esa clausula a partir de ahora.
//
// No hay borrar: una clausula que ya salio en cotizaciones se DESACTIVA (deja de ofrecerse).
// Reordenar con flechas, no arrastrando: funciona igual con el dedo a 360 px.

import React, { useCallback, useEffect, useState } from 'react';
import { ScrollText, Plus, ArrowUp, ArrowDown, Save } from 'lucide-react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';
import { useUsuario } from '../context/UsuarioContext';

const lbl = { fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 6, fontWeight: 600 };
const inp = { ...estiloInput, fontSize: 16, minHeight: 44 };
const area = { ...estiloInput, fontSize: 15, minHeight: 90, resize: 'vertical', fontFamily: PP, lineHeight: 1.45 };
const NUEVA = { id: null, titulo: '', texto: '', texto_anticipo_parcial: '', fija: false, activo: true, familias: [] };

export default function ClausulasCotizacionAdmin({ compacto = false }) {
  const { accesoTotal } = useUsuario();
  const puedeEditar = !!accesoTotal; // mismo criterio que soloDireccion en el servidor
  const [lista, setLista] = useState(null);
  const [familias, setFamilias] = useState([]);
  const [editando, setEditando] = useState(null); // copia de la clausula en edicion (id null = nueva)
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const avisar = (txt, tipo = 'ok') => { setToast({ txt, tipo }); setTimeout(() => setToast(null), 5000); };

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clausulas-cotizacion?todas=1`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError('No tienes acceso a los términos y condiciones.'); return; }
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'No se pudieron leer');
      setLista(j.clausulas || []); setFamilias(j.familias || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!editando) return;
    if (!editando.titulo.trim() || !editando.texto.trim()) return avisar('El título y el texto son obligatorios.', 'error');
    setGuardando(true);
    try {
      const esNueva = !editando.id;
      const r = await fetch(`${API_BASE_CLI}/api/clausulas-cotizacion${esNueva ? '' : '/' + editando.id}`, {
        method: esNueva ? 'POST' : 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ titulo: editando.titulo, texto: editando.texto, texto_anticipo_parcial: editando.texto_anticipo_parcial || null, fija: editando.fija, activo: editando.activo, familias: editando.familias }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo guardar');
      setEditando(null); await cargar();
      avisar(esNueva ? 'Cláusula creada.' : 'Cláusula guardada. Las cotizaciones ya guardadas conservan su texto anterior.');
    } catch (e) { avisar(e.message, 'error'); }
    finally { setGuardando(false); }
  };

  const alternarActiva = async (c) => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clausulas-cotizacion/${c.id}`, { method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ activo: !c.activo }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo cambiar');
      await cargar();
      avisar(c.activo ? `"${c.titulo}" desactivada: ya no se ofrece en cotizaciones nuevas.` : `"${c.titulo}" activada.`);
    } catch (e) { avisar(e.message, 'error'); }
  };

  const mover = async (idx, delta) => {
    const n = [...lista];
    const j = idx + delta;
    if (j < 0 || j >= n.length) return;
    [n[idx], n[j]] = [n[j], n[idx]];
    setLista(n);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clausulas-cotizacion/orden`, { method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: n.map(c => c.id) }) });
      const jj = await r.json().catch(() => ({}));
      if (!r.ok || !jj.ok) throw new Error(jj.error || 'No se pudo guardar el orden');
      setLista(jj.clausulas);
    } catch (e) { avisar(e.message, 'error'); cargar(); }
  };

  const etiquetaFam = (id) => (familias.find(f => f.id === id) || {}).etiqueta || id;

  return (
    <div style={{ ...estiloTarjeta, padding: 18, fontFamily: PP, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ScrollText size={18} color="#265F97" /> Términos y condiciones de las cotizaciones
        </div>
        {puedeEditar && !editando && (
          <button type="button" style={{ ...estiloBotonSecundario, marginLeft: 'auto', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={() => setEditando({ ...NUEVA })}><Plus size={16} /> Nueva cláusula</button>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: COLOR_GRIS_CLARO, lineHeight: 1.5, marginBottom: 12 }}>
        Salen al final de cada cotización. Las <strong>fijas</strong> van siempre; las demás se marcan en el editor
        y vienen preseleccionadas según la familia del trabajo (solo si la cotización lleva algún servicio).
        <br />Editar una cláusula <strong>no cambia las cotizaciones ya guardadas</strong>: cada una conserva el texto con el que se guardó.
        <br />Marcas que se sustituyen con los datos de cada cotización: <code>{'{vigencia_dias}'}</code> (días de vigencia),
        {' '}<code>{'{anticipo_pct}'}</code> (% de anticipo) y <code>{'{saldo_pct}'}</code> (lo que queda por pagar).
        {!puedeEditar && <><br />Solo Dirección puede editarlas.</>}
      </div>

      {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
      {!error && !lista && <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando…</div>}

      {editando && (
        <div style={{ marginBottom: 14, padding: 14, border: '1px dashed #265F97', borderRadius: 12, background: '#F8FBFF' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>{editando.id ? 'Editar cláusula' : 'Nueva cláusula'}</div>
          <div style={lbl}>Título</div>
          <input style={inp} value={editando.titulo} maxLength={120} onChange={e => setEditando(c => ({ ...c, titulo: e.target.value }))} placeholder="Ej. Forma de pago" />
          <div style={{ ...lbl, marginTop: 12 }}>Texto completo (sale tal cual en el PDF)</div>
          <textarea style={area} value={editando.texto} maxLength={3000} onChange={e => setEditando(c => ({ ...c, texto: e.target.value }))} />
          <div style={{ ...lbl, marginTop: 12 }}>Texto si el anticipo es MENOR al 100% (opcional; vacío = el mismo texto)</div>
          <textarea style={{ ...area, minHeight: 70 }} value={editando.texto_anticipo_parcial || ''} maxLength={3000}
            placeholder="Solo para cláusulas que hablan del pago. Ej.: {anticipo_pct}% de anticipo… El {saldo_pct}% restante…"
            onChange={e => setEditando(c => ({ ...c, texto_anticipo_parcial: e.target.value }))} />
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10, fontSize: 13 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 20, height: 20 }} checked={!!editando.fija} onChange={e => setEditando(c => ({ ...c, fija: e.target.checked }))} /> Fija (va siempre, no se puede desmarcar)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 20, height: 20 }} checked={!!editando.activo} onChange={e => setEditando(c => ({ ...c, activo: e.target.checked }))} /> Activa
            </label>
          </div>
          {!editando.fija && (
            <div style={{ marginTop: 8 }}>
              <div style={lbl}>Viene marcada de entrada en estas familias de trabajo</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {familias.map(f => {
                  const on = (editando.familias || []).includes(f.id);
                  return (
                    <button key={f.id} type="button" aria-pressed={on}
                      onClick={() => setEditando(c => ({ ...c, familias: on ? c.familias.filter(x => x !== f.id) : [...(c.familias || []), f.id] }))}
                      style={{ minHeight: 40, padding: '6px 12px', borderRadius: 99, fontFamily: PP, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                        border: `1px solid ${on ? '#265F97' : COLOR_BORDE}`, background: on ? '#265F97' : '#fff', color: on ? '#fff' : COLOR_GRIS }}>
                      {f.etiqueta}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {editando.id && (
            <div style={{ fontSize: 12, color: '#8a5a1b', background: '#FFF6E5', border: '1px solid #F3D9A4', borderRadius: 8, padding: '8px 10px', marginTop: 12 }}>
              Las cotizaciones ya guardadas que llevan esta cláusula seguirán diciendo exactamente lo mismo que el día que se guardaron.
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <button type="button" style={{ ...estiloBotonPrimario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={guardando} onClick={guardar}><Save size={16} /> {guardando ? 'Guardando…' : 'Guardar cláusula'}</button>
            <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44 }} onClick={() => setEditando(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {lista && (
        <div style={{ display: 'grid', gap: 8 }}>
          {lista.map((c, i) => (
            <div key={c.id} style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 12, opacity: c.activo ? 1 : 0.6, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: compacto ? 'wrap' : 'nowrap', minWidth: 0 }}>
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <strong style={{ fontSize: 13.5, color: COLOR_OSCURO }}>{i + 1}. {c.titulo}</strong>
                  {c.fija && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: '#265F97' }}>Fija</span>}
                  {!c.activo && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#f4f5f6', color: COLOR_GRIS }}>Desactivada</span>}
                </div>
                <div style={{ fontSize: 12.5, color: COLOR_GRIS, lineHeight: 1.45, marginTop: 4, wordBreak: 'break-word' }}>{c.texto}</div>
                {c.texto_anticipo_parcial && (
                  <div style={{ fontSize: 12.5, color: COLOR_GRIS, lineHeight: 1.45, marginTop: 4, wordBreak: 'break-word' }}>
                    <strong style={{ fontWeight: 600 }}>Con anticipo menor al 100%:</strong> {c.texto_anticipo_parcial}
                  </div>
                )}
                {!c.fija && (
                  <div style={{ fontSize: 11.5, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
                    {(c.familias || []).length ? `Preseleccionada en: ${c.familias.map(etiquetaFam).join(', ')}` : 'No viene preseleccionada en ninguna familia'}
                  </div>
                )}
              </div>
              {puedeEditar && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" aria-label={`Subir ${c.titulo}`} disabled={i === 0} onClick={() => mover(i, -1)} style={{ ...estiloBotonSecundario, minHeight: 44, minWidth: 44, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: i === 0 ? 0.4 : 1 }}><ArrowUp size={16} /></button>
                  <button type="button" aria-label={`Bajar ${c.titulo}`} disabled={i === lista.length - 1} onClick={() => mover(i, 1)} style={{ ...estiloBotonSecundario, minHeight: 44, minWidth: 44, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: i === lista.length - 1 ? 0.4 : 1 }}><ArrowDown size={16} /></button>
                  <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44 }} onClick={() => setEditando({ ...c, familias: c.familias || [], texto_anticipo_parcial: c.texto_anticipo_parcial || '' })}>Editar</button>
                  <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44 }} onClick={() => alternarActiva(c)}>{c.activo ? 'Desactivar' : 'Activar'}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {toast && <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: toast.tipo === 'error' ? '#dc2626' : '#16a34a' }}>{toast.txt}</div>}
    </div>
  );
}
